import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnDestroy,
    signal,
} from '@angular/core';
import cytoscape, { Core, CytoscapeOptions, ElementDefinition, ElementsDefinition, NodeSingular } from 'cytoscape';
import { CytoscapejsComponent } from 'ngx-cytoscapejs';
import { debounce, firstValueFrom, fromEvent, interval, Subscription, tap } from 'rxjs';

import { LabelBadgeComponent } from '@connect-services/client/components/label-badge/label-badge.component';
import { SelectSynopticPanelComponent } from '@connect-services/client/components/synoptic/select-synoptic-panel/select-synoptic-panel.component';
import { SynopticNodeData } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import {
    SelectedNode,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { SynopticPlanComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan.component';
import { SynopticWindowButtonComponent } from '@connect-services/client/components/synoptic/synoptic-window-button/synoptic-window-button.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    NetworkPathBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    GenericSynopticItem,
    GraceThdSynopticItem,
    PlanTypeEnum,
    Synoptic,
    SynopticBayPlanItem,
    SynopticBoxPlanItem,
    SynopticService,
} from '@connect-services/client/sdk/api';
import {
    BroadcastChannelService,
    SynopticBoxPlanData,
} from '@connect-services/client/services/broadcast-channel.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { NetworkPathService } from '@connect-services/client/services/network-path/network-path.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SynopticPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { ExportSynopticService } from '@connect-services/client/services/synoptic/export-synoptic.service';
import { GenericSynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/generic-synoptic-graph-builder.service';
import { GraceThdSynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/grace-thd-synoptic-graph-builder.service';
import {
    SelectedSynoptic,
    SelectSynopticService,
} from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticContextMenuService } from '@connect-services/client/services/synoptic/synoptic-context-menu.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import { SynopticPlanInteractionService } from '@connect-services/client/services/synoptic/synoptic-plan-interaction.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CytoscapejsComponent,
        LabelBadgeComponent,
        LoadingComponent,
        RessourcesModule,
        SelectSynopticPanelComponent,
        SynopticPlanComponent,
        SynopticWindowButtonComponent,
    ],
    selector: 'app-synoptic-graph',
    styleUrls: ['./synoptic-graph.component.scss', '../synoptic-context-menu.scss'],
    templateUrl: './synoptic-graph.component.html',
})
export class SynopticGraphComponent implements AfterViewInit, OnDestroy {
    public readonly cytoscapeOptions = computed<CytoscapeOptions | undefined>(() => {
        const synoptic = this.synoptic();
        const selectedDeposit = this.selectSynopticService.selectedDeposit();
        if (!synoptic || !selectedDeposit) {
            return;
        }

        if (selectedDeposit.standard.alias.includes('gracethd')) {
            return this.graceThdSynopticBuilderService.buildSynoptic(synoptic.data as GraceThdSynopticItem[]);
        }

        return this.genericSynopticBuilderService.buildSynoptic(synoptic.data as GenericSynopticItem[]);
    });
    public readonly isLoadingPlan = signal(false);
    public readonly isSplit = signal(false);
    public readonly isSynopticPlanDisplayed = computed(() => {
        const hasNode = !!this.synopticGraphService.focusedNode();
        const hasItems = !!this.planItems().length;

        return (this.homeService.isExternalBoxPlanPage() || this.isSplit()) && (hasNode || hasItems);
    });
    public readonly planItems = signal<SynopticPlanItem[]>([]);
    public readonly synopticGraphHeightClass = computed(() => {
        return {
            'h-1/2': this.isSplit() && this.homeService.isExternalPage(),
            'h-1/3': this.isSplit() && !this.homeService.isExternalPage(),
            'h-full': !this.isSplit(),
        };
    });
    public readonly synopticPlanStyle = computed(() => {
        return {
            'boxPlan-content--h-full': this.homeService.isExternalBoxPlanPage(),
            'boxPlan-content--tall': !this.homeService.isExternalPage() && !this.homeService.isExternalBoxPlanPage(),
            'mt-4': !this.homeService.isExternalBoxPlanPage(),
        };
    });

    protected readonly ButtonType = ButtonType;
    protected hasBPInExternalWindow = false;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly synopticContentStyle = computed(() => {
        return {
            'h-0': this.selectSynopticService.isSynopticGenerating(),
            'h-full': !this.selectSynopticService.isSynopticGenerating(),
            'overflow-hidden': this.selectSynopticService.isSynopticGenerating(),
        };
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly exportSynopticService = inject(ExportSynopticService);
    protected readonly homeService = inject(HomeService);
    protected readonly selectSynopticService = inject(SelectSynopticService);
    protected readonly synopticGraphService = inject(SynopticGraphService);
    protected readonly synopticPlanEditService = inject(SynopticPlanEditService);

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private currentSynoptic: SelectedSynoptic | null = null;
    private readonly isGraceThd = computed<boolean>(() => {
        return !!this.selectSynopticService.selectedDeposit()?.standard.alias.includes('gracethd');
    });
    private resize!: Subscription;
    private readonly synoptic = signal<Synoptic | null>(null);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly genericSynopticBuilderService = inject(GenericSynopticGraphBuilderService);
    private readonly graceThdSynopticBuilderService = inject(GraceThdSynopticGraphBuilderService);
    private readonly networkPathBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.NETWORK_PATH);
    private readonly networkPathService = inject(NetworkPathService);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly synopticContextMenuService = inject(SynopticContextMenuService);
    private readonly synopticPlanInteractionService = inject(SynopticPlanInteractionService);
    private readonly synopticService = inject(SynopticService);

    public constructor() {
        effect(async () => {
            if (!this.synopticGraphService.focusedNode() || this.homeService.isExternalBoxPlanPage()) {
                return;
            }

            await this.displayPlan(this.synopticGraphService.focusedNode()!);
        });
        effect(() => {
            if (!this.selectSynopticService.selectedSynoptic()) {
                return;
            }

            if (this.currentSynoptic?.znUuid === this.selectSynopticService.selectedSynoptic()!.znUuid) {
                return;
            }

            this.currentSynoptic = this.selectSynopticService.selectedSynoptic();

            this.synoptic.set(this.selectSynopticService.selectedSynoptic());

            this.selectSynopticService.isLoading.set(false);
        });

        if (this.homeService.isExternalBoxPlanPage()) {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<null>>{
                isOpening: true,
                source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
            });
        }

        this.broadcastChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<SynopticBoxPlanData & SynopticNodeData>>,
        ): Promise<void> => {
            this.handleBroadcastChannelBoxPlanEvents(event);
            await this.handleBroadcastChannelSynopticMovingEvent(event);
        };

        if (this.homeService.isExternalPage()) {
            this.networkPathBroadcastChannel.onmessage = (
                event: MessageEvent<BroadcastChannelEvent<NetworkPathBroadcastChannelData>>,
            ): void => {
                this.handleNetworkPathBroadcastEvent(event);
            };
        }
    }

    public close(): void {
        this.isSplit.set(false);
        this.planItems.set([]);
        this.synopticGraphService.focusedNode.set(null);
        this.synopticPlanInteractionService.selectedCassetteKey.set(null);

        this.removeHighlight();

        if (this.homeService.isExternalBoxPlanPage()) {
            this.broadcastChannelService.isSynopticOpenInExternalPage.set(false);

            window.close();
        }
    }

    public doReady(cytoscape: Core & { contextMenus?: (options: { [key: string]: unknown }) => void }): void {
        this.synopticGraphService.focusedNode.set(null);
        this.synopticGraphService.setCytoscapeInstance(cytoscape);

        if (cytoscape.contextMenus) {
            const menus = this.synopticContextMenuService.generateMenu(this.isGraceThd(), this.synoptic);
            cytoscape.contextMenus(menus);
        }

        cytoscape.ready(() => {
            cytoscape.maxZoom(1.8);

            cytoscape.minZoom(0.87 / this.synoptic()!.data.length);

            cytoscape.on('tap', 'node', (event) => {
                const node: NodeSingular = event.target;
                this.synopticGraphService.switchVisibility(node, cytoscape);
            });

            cytoscape.on('drag', 'node', (evt) => {
                const node = evt.target;
                this.synopticGraphService.setToTop(node, cytoscape);
            });

            this.selectSynopticService.isLoading.set(false);
        });

        cytoscape.one('render', () => {
            this.selectSynopticService.isSynopticGenerating.set(false);

            requestAnimationFrame(() => {
                cytoscape.resize();
                cytoscape.fit();

                const contextMenuElement: HTMLElement | null = document.querySelector('.custom-context-menu');
                contextMenuElement?.classList.add('opacity-1');
            });
        });
    }

    public ngAfterViewInit(): void {
        this.resize = fromEvent(window, 'resize')
            .pipe(
                tap(() => {
                    if (this.isSplit()) {
                        this.isLoadingPlan.set(true);
                    }
                }),
                debounce(() => interval(1000)),
            )
            .subscribe(() => {
                if (this.isSplit()) {
                    this.isLoadingPlan.set(false);
                    this.synopticGraphService.zoomPlanElement(this.synopticGraphService.focusedNode());
                }
            });
    }

    public ngOnDestroy(): void {
        this.networkPathBroadcastChannel.close();

        this.close();
        this.resize.unsubscribe();
    }

    private async displayPlan(node: SelectedNode): Promise<void> {
        this.isLoadingPlan.set(true);
        this.selectSynopticService.isSynopticGenerating.set(false);

        try {
            const depositId: string = this.selectSynopticService.selectedDeposit()!.id;
            const type: PlanTypeEnum | null = this.synopticGraphService.planType();

            const { items, isSplit } = await this.loadPlanItemsByType(node, type, depositId);

            this.isSplit.set(isSplit && !this.hasBPInExternalWindow);
            this.planItems.set(items ?? []);

            setTimeout(() => {
                this.highlightCurrentNode(node.rawNode);
                this.synopticGraphService.zoomPlanElement(node);
            }, 1000);

            this.broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticBoxPlanData>>{
                additionalData: {
                    cyJson: this.synopticGraphService.cy?.json(),
                    isLoading: false,
                    planItems: this.planItems()!,
                    selectedDeposit: this.selectSynopticService.selectedDeposit(),
                    selectedNodeData: this.synopticGraphService.focusedNode()!.dataNode,
                    selectedSynoptic: this.selectSynopticService.selectedSynoptic(),
                },
                isOpening: true,
                message: BroadcastChannelMessageEnum.CHANGE_BOX_PLAN,
                source: BroadcastChannelSourceEnum.GRAPH,
            });
        } catch {
            this.isSplit.set(false);
            this.planItems.set([]);
        } finally {
            this.isLoadingPlan.set(false);
        }
    }

    private async handleBroadcastChannelBoxPlanEvents(
        event: MessageEvent<BroadcastChannelEvent<SynopticBoxPlanData>>,
    ): Promise<void> {
        if (event.data.isClosing && event.data.source === BroadcastChannelSourceEnum.SYNOPTIC_PLAN) {
            this.isSplit.set(true);
            this.hasBPInExternalWindow = false;

            const editSessionId = this.projectEditModeService.getEditSession()?.id;
            if (!editSessionId) {
                return;
            }

            const node = this.synopticGraphService.focusedNode();
            if (!node) {
                return;
            }

            await this.displayPlan(node);

            return;
        }

        this.openBoxPlanInExternalWindow(event);
        this.sendBoxPlanDataToExternalWindow(event);
    }

    private async handleBroadcastChannelSynopticMovingEvent(
        event: MessageEvent<BroadcastChannelEvent<SynopticNodeData>>,
    ): Promise<void> {
        const nodeData: SynopticNodeData | undefined = event.data.additionalData;

        if (
            event.data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN ||
            event.data.message !== BroadcastChannelMessageEnum.MOVE_SYNOPTIC ||
            !nodeData
        ) {
            return;
        }

        this.recreateNode(nodeData);
        await this.displayPlan(this.synopticGraphService.focusedNode()!);
    }

    private handleNetworkPathBroadcastEvent(
        event: MessageEvent<BroadcastChannelEvent<NetworkPathBroadcastChannelData>>,
    ): void {
        const data = event.data;

        if (data.source !== BroadcastChannelSourceEnum.NETWORK_PATH) {
            return;
        }

        if (data.message === BroadcastChannelMessageEnum.RESET_NETWORK_PATH) {
            this.synopticGraphService.resetNetworkPathFilter(true);

            return;
        }

        if (data.message !== BroadcastChannelMessageEnum.DISPLAY_NETWORK_PATH || !data.additionalData) {
            return;
        }

        this.synopticGraphService.filterByNetworkPath(
            new Set(data.additionalData.nodeUuids),
            new Set(data.additionalData.edgeUuids),
        );
    }

    private highlightCurrentNode(node: NodeSingular): void {
        this.removeHighlight();
        node.toggleClass('isFocusedNode', true);
    }

    private async loadPlanItemsByType(
        node: SelectedNode,
        type: PlanTypeEnum | null,
        depositId: string,
    ): Promise<{ isSplit: boolean; items: SynopticPlanItem[] | null }> {
        const editSessionId = this.projectEditModeService.getEditSession()?.id;

        switch (type) {
            case PlanTypeEnum.BOX: {
                const boxId: string = node.dataNode.uuid;
                const items: SynopticBoxPlanItem[] = await firstValueFrom(
                    this.synopticService.synopticControllerGetBoxPlan({ boxId, depositId, editSessionId }),
                );

                return { isSplit: true, items };
            }
            case PlanTypeEnum.BAY: {
                const bayId: string = node.dataNode.uuid;
                const items: SynopticBayPlanItem[] = await firstValueFrom(
                    this.synopticService.synopticControllerGetBayPlan({ bayId, depositId, editSessionId }),
                );

                return { isSplit: true, items };
            }
            default: {
                return { isSplit: false, items: null };
            }
        }
    }

    private openBoxPlanInExternalWindow(event: MessageEvent<BroadcastChannelEvent<SynopticBoxPlanData>>): void {
        const properties: SynopticBoxPlanData | undefined = event.data.additionalData;
        if (
            !this.homeService.isExternalBoxPlanPage() ||
            !event.data.isOpening ||
            event.data.source !== BroadcastChannelSourceEnum.GRAPH ||
            !properties
        ) {
            return;
        }

        if (properties.cyJson) {
            this.synopticGraphService.cy = cytoscape({
                elements: (properties.cyJson as unknown as { elements: ElementDefinition[] | ElementsDefinition })
                    .elements,
                headless: true,
            });
        }

        this.selectSynopticService.selectedDeposit.set(properties.selectedDeposit);
        this.selectSynopticService.selectedSynoptic.set(properties.selectedSynoptic);
        this.isLoadingPlan.set(properties.isLoading);
        this.planItems.set(properties.planItems ?? []);

        this.selectSynopticService.isLoading.set(false);
        this.selectSynopticService.isSynopticGenerating.set(false);

        if (properties.selectedNodeData) {
            this.recreateNode(properties.selectedNodeData);
        }
    }

    private recreateNode(nodeData: SynopticNodeData): void {
        if (!this.synopticGraphService.cy) {
            return;
        }

        const node = this.synopticGraphService.cy.getElementById(nodeData.id);

        this.synopticGraphService.focusedNode.set({
            dataNode: nodeData,
            rawNode: node,
        });
    }

    private removeHighlight(): void {
        if (!this.synopticGraphService.cy) {
            return;
        }

        const activeNode = this.synopticGraphService.cy.filter((n) => n.hasClass('isFocusedNode'));

        if (activeNode.length) {
            activeNode.toggleClass('isFocusedNode', false);
        }
    }

    private sendBoxPlanDataToExternalWindow(event: MessageEvent<BroadcastChannelEvent<SynopticBoxPlanData>>): void {
        if (
            !this.homeService.isExternalPage() ||
            !event.data.isOpening ||
            event.data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN
        ) {
            return;
        }

        this.isSplit.set(false);
        this.hasBPInExternalWindow = true;
        this.broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticBoxPlanData>>{
            additionalData: {
                cyJson: this.synopticGraphService.cy?.json(),
                isLoading: this.isLoadingPlan(),
                planItems: this.planItems()!,
                selectedDeposit: this.selectSynopticService.selectedDeposit(),
                selectedNodeData: this.synopticGraphService.focusedNode()!.dataNode,
                selectedSynoptic: this.selectSynopticService.selectedSynoptic(),
            },
            isOpening: true,
            message: BroadcastChannelMessageEnum.CHANGE_BOX_PLAN,
            source: BroadcastChannelSourceEnum.GRAPH,
        });
    }
}
